import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import awsConfig from '../../../aws-exports';
import { UserAuthService } from 'app/core/user/user.service';
import {
    from,
    Observable,
    of,
    BehaviorSubject,
    finalize,
    firstValueFrom
} from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { JwtPayload } from '@aws-amplify/core/internals/utils';
import { UpdateUserInput, User } from '@app/core/services/api/API.service';
import {
    ClientOptionService,
    ClientService,
    LogsPUsersService,
    ProviderService,
    UserService
} from '@services/index';
import {
    signIn,
    signOut,
    fetchAuthSession,
    SignInInput,
    SignInOutput
} from 'aws-amplify/auth';
import { Router } from '@angular/router';
import {
    CredentialsService,
    CredentialsCurrentUser
} from '../authentication/credentials.service';
import { UtilService } from '@app/shared/services/util.service';
import { ROLES } from '@app/constants';
import { environment } from '@env/environment';
import { ClientSettingService } from '@services/index';
import { UserClientOptionService } from '@app/shared/services/preloaded/auth/user-client-option.service';
import { UserBrandSettingPreloadService } from '@app/shared/services/preloaded/auth/user-brand-setting.service';
import { PremiumSessionCartService } from '@app/shared/services/preloaded/auth/cart.service';

export interface SignInResult extends SignInOutput {
    user: User;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _accessToken = '';
    private _httpClient = inject(HttpClient);
    private _userAuthService = inject(UserAuthService);
    private _userService = inject(UserService);
    private _credentialsService = inject(CredentialsService);
    private _utilService = inject(UtilService);
    private _clientOptionService = inject(ClientOptionService);
    private _clientService = inject(ClientService);
    private _providerService = inject(ProviderService);
    private _logsPUserService = inject(LogsPUsersService);
    private _router = inject(Router);
    private _clientSettingService = inject(ClientSettingService);
    private _injector = inject(Injector);
    public ROLES = ROLES;

    private _cachedSession: any = null;
    private _sessionCacheTime: number = 0;
    private _sessionCacheTTL: number = 5 * 60 * 1000;
    private _isFetchingSession: boolean = false;
    private _sessionPromise: Promise<any> | null = null;
    private _cachedError: any = null;
    private _errorCacheTime: number = 0;
    private _errorCacheTTL: number = 30 * 1000;
    private _cachedHeaders: HttpHeaders | null = null;
    private _headersCacheTime: number = 0;
    private _headersCacheTTL: number = 2 * 60 * 1000;
    private _isGeneratingHeaders: boolean = false;
    private _headersPromise: Promise<HttpHeaders> | null = null;
    private _lastHeadersUserId: string | null = null;
    private _checkInFlight$: Observable<boolean> | null = null;
    private _setCredentialsPromise: Promise<unknown> | null = null;
    private _setCredentialsUserId: string | null = null;
    private _lastHydratedUserId: string | null = null;
    private extractDomain(email: string): string {
        return email.split('@')[1]?.toLowerCase() || '';
    }

    /** Emite siempre un valor inicial (false) para que los suscriptores no queden bloqueados. */
    private _authenticatedSubject = new BehaviorSubject<boolean>(false);

    /** Indica si hay indicios de sesión persistida (evita fetchAuthSession en invitados). */
    hasStoredSession(): boolean {
        if (localStorage.getItem('credentials')) {
            return true;
        }

        const cognitoPath = `CognitoIdentityServiceProvider.${awsConfig.aws_user_pools_web_client_id}`;
        return !!localStorage.getItem(`${cognitoPath}.LastAuthUser`);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get cached auth session or fetch if not cached
     * This prevents multiple calls to fetchAuthSession
     */
    private async getCachedAuthSession(
        forceRefresh: boolean = false
    ): Promise<any> {
        const now = Date.now();
        const isCacheValid =
            this._cachedSession &&
            now - this._sessionCacheTime < this._sessionCacheTTL;

        if (isCacheValid && !forceRefresh) {
            return this._cachedSession;
        }

        const isErrorCacheValid =
            this._cachedError &&
            now - this._errorCacheTime < this._errorCacheTTL;

        if (isErrorCacheValid && !forceRefresh) {
            throw this._cachedError;
        }

        if (this._isFetchingSession && this._sessionPromise) {
            return this._sessionPromise;
        }

        this._isFetchingSession = true;
        this._sessionPromise = fetchAuthSession()
            .then(session => {
                this._cachedSession = session;
                this._sessionCacheTime = Date.now();
                this._cachedError = null;
                this._errorCacheTime = 0;
                this._isFetchingSession = false;
                this._sessionPromise = null;
                return session;
            })
            .catch(error => {
                this._isFetchingSession = false;
                this._sessionPromise = null;
                this._cachedSession = null;
                this._sessionCacheTime = 0;
                this._cachedError = error;
                this._errorCacheTime = Date.now();
                throw error;
            });

        return this._sessionPromise;
    }

    /**
     * Get auth session (public method for other services)
     * Returns cached session or null if user is not authenticated
     */
    async getAuthSession(): Promise<any> {
        try {
            return await this.getCachedAuthSession();
        } catch (error) {
            return null;
        }
    }

    /**
     * Clear cached session (useful after sign out or session changes)
     */
    private clearCachedSession(): void {
        this._cachedSession = null;
        this._sessionCacheTime = 0;
        this._sessionPromise = null;
        this._isFetchingSession = false;
        this._cachedError = null;
        this._errorCacheTime = 0;
        this._cachedHeaders = null;
        this._headersCacheTime = 0;
        this._lastHeadersUserId = null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get accessToken(): Observable<string> {
        return new Observable<string>(observer => {
            this.getCachedAuthSession()
                .then(({ tokens }) => {
                    this._accessToken = tokens.accessToken.toString();
                    observer.next(tokens.accessToken.toString());
                    observer.complete();
                })
                .catch(error => {
                    observer.error(error);
                });
        });
    }

    get authenticated$(): Observable<boolean> {
        return this._authenticatedSubject.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Generate HTTP headers for API requests
     * Includes authentication token and user-specific headers when available
     * Cached to prevent multiple calls to fetchAuthSession
     *
     * @returns Promise<HttpHeaders> - Headers with authentication and user context
     */
    async generateHeaders(): Promise<HttpHeaders> {
        const currentUser = this._credentialsService.credentialsCurrentUser;
        const now = Date.now();
        const userId = currentUser?.id || 'no-user';
        const isHeadersCacheValid =
            this._cachedHeaders &&
            now - this._headersCacheTime < this._headersCacheTTL &&
            this._lastHeadersUserId === userId;

        if (isHeadersCacheValid) {
            return this._cachedHeaders;
        }

        if (this._isGeneratingHeaders && this._headersPromise) {
            return this._headersPromise;
        }

        this._isGeneratingHeaders = true;
        this._headersPromise = this._generateHeadersInternal(currentUser);

        this._headersPromise
            .then(headers => {
                this._cachedHeaders = headers;
                this._headersCacheTime = Date.now();
                this._lastHeadersUserId = userId;
                this._isGeneratingHeaders = false;
                this._headersPromise = null;
            })
            .catch(() => {
                this._isGeneratingHeaders = false;
                this._headersPromise = null;
            });

        return this._headersPromise;
    }

    private async _generateHeadersInternal(
        currentUser: CredentialsCurrentUser | null
    ): Promise<HttpHeaders> {
        const baseHeaders: { [key: string]: string } = {
            'Content-Type': 'application/json'
        };

        if (!currentUser) {
            return new HttpHeaders(baseHeaders);
        }

        try {
            const { tokens } = await this.getCachedAuthSession();
            const token = tokens?.idToken?.toString();

            if (token) {
                baseHeaders.Authorization = token;
            }
        } catch (error) {}

        if (currentUser?.buyerCookie?.trim()) {
            return new HttpHeaders({
                ...baseHeaders,
                'X-User-Sub': currentUser.buyerCookie,
                'X-Is-Coupa': currentUser.isCoupa ? 'true' : 'false'
            });
        }

        return new HttpHeaders(baseHeaders);
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Verify failed logins,
     * 0: 3 failed logins,
     * 1: Allow login,
     * 2: Coupa user,
     * 3: Not found user
     *
     * @param {string} email - Correo electrónico a validar
     */
    async verifyFailedLogins(email: string): Promise<number> {
        try {
            const users: User[] = await this._userService.getUserEmail(email);
            if (users[0]) {
                // Previous Coupa login
                if (users[0].isCoupa) {
                    return 2;
                }
                // Natural login
                if ((users[0].failedLogins ? users[0].failedLogins : 0) >= 3) {
                    return 0;
                } else {
                    return 1;
                }
            } else {
                return 3;
            }
        } catch (err) {
            console.error(err);
            return 0;
        }
    }

    /** */

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn({ username, password }: SignInInput): Observable<SignInResult> {
        return from(this.signInWithAmplify(username, password));
    }

    private isAlreadySignedInError(error: unknown): boolean {
        if (typeof error === 'string') {
            return (
                error === 'There is already a signed in user.' ||
                error === 'User is already logged in.'
            );
        }

        const err = error as { name?: string; message?: string };
        return (
            err?.name === 'UserAlreadyAuthenticatedException' ||
            err?.message === 'There is already a signed in user.' ||
            err?.message === 'User is already logged in.'
        );
    }

    private sessionMatchesUsername(
        idToken: JwtPayload,
        username: string
    ): boolean {
        const target = username.toLowerCase();
        const email = String(idToken.email ?? '').toLowerCase();
        const cognitoUsername = String(
            idToken['cognito:username'] ?? ''
        ).toLowerCase();

        return email === target || cognitoUsername === target;
    }

    private async tryCompleteSignInFromSession(
        username: string
    ): Promise<SignInResult | null> {
        try {
            this.clearCachedSession();
            const session = await fetchAuthSession({ forceRefresh: true });
            const idToken = session.tokens?.idToken?.payload as
                | JwtPayload
                | undefined;

            if (!idToken || !this.sessionMatchesUsername(idToken, username)) {
                return null;
            }

            const user = await this.setCredentials(idToken);
            return {
                isSignedIn: true,
                nextStep: { signInStep: 'DONE' },
                user
            };
        } catch {
            return null;
        }
    }

    private async hydrateSignInResult({
        isSignedIn,
        nextStep
    }: SignInOutput): Promise<SignInResult> {
        this.clearCachedSession();
        const idToken: JwtPayload = (await this.getCachedAuthSession(true))
            .tokens.idToken.payload;
        const user = await this.setCredentials(idToken);
        return { isSignedIn, nextStep, user };
    }

    private async signInWithAmplify(
        username: string,
        password: string,
        hasRetried = false
    ): Promise<SignInResult> {
        try {
            if (!hasRetried && this.hasStoredSession()) {
                const existing = await this.tryCompleteSignInFromSession(
                    username
                );
                if (existing) {
                    return existing;
                }
            }

            const { isSignedIn, nextStep } = await signIn({
                username,
                password
            });
            return this.hydrateSignInResult({ isSignedIn, nextStep });
        } catch (error) {
            if (this.isAlreadySignedInError(error)) {
                const existing = await this.tryCompleteSignInFromSession(
                    username
                );
                if (existing) {
                    return existing;
                }

                if (!hasRetried) {
                    await firstValueFrom(this.signOutHandler());
                    await this._utilService.delay(150);
                    return this.signInWithAmplify(username, password, true);
                }
            }

            console.log('error signing in:', error);
            throw error;
        }
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        return from(this.ensureCredentialsFromSession());
    }

    /**
     * setCredentials
     * Stores user attributes
     */
    async setCredentials(idToken: JwtPayload): Promise<any> {
        const userId = idToken.sub;

        if (
            this._lastHydratedUserId === userId &&
            this._credentialsService.credentialsCurrentUser?.id === userId
        ) {
            this._authenticated = true;
            this._authenticatedSubject.next(true);
            return this._credentialsService.credentialsCurrentUser;
        }

        if (
            this._setCredentialsPromise &&
            this._setCredentialsUserId === userId
        ) {
            return this._setCredentialsPromise;
        }

        this._setCredentialsUserId = userId;
        this._setCredentialsPromise = this.hydrateCredentialsFromToken(
            idToken
        ).finally(() => {
            this._setCredentialsPromise = null;
            this._setCredentialsUserId = null;
        });

        return this._setCredentialsPromise;
    }

    private async ensureCredentialsFromSession(): Promise<string> {
        const { tokens } = await this.getCachedAuthSession();
        this._accessToken = tokens.accessToken.toString();
        const idToken: JwtPayload = tokens.idToken.payload;
        await this.setCredentials(idToken);
        return this._accessToken;
    }

    private async hydrateCredentialsFromToken(
        idToken: JwtPayload
    ): Promise<any> {
        if (idToken.sub !== environment.coupaSub) {
            let userBD = await this._userService.findUserById(idToken.sub);
            let company = null;
            if (!userBD && idToken.email) {
                const usersByEmail = await this._userService.getUserEmail(
                    String(idToken.email).toLowerCase()
                );
                userBD = usersByEmail?.[0] ?? null;
            }
            if (!userBD) {
                console.log('NOT FOUND');
                console.log(
                    idToken.sub,
                    idToken.name as string,
                    idToken.email as string
                );
                throw new Error('User not found');
            }
            const companyId = userBD?.companyId;

            if (
                (companyId && userBD.role === this.ROLES.AGENCIA) ||
                userBD.role === this.ROLES.PROVEEDOR
            ) {
                const companyObj = await this._providerService.getProvider(
                    companyId
                );
                company = {
                    comertial_name: companyObj['comertial_name'],
                    id: companyObj['id'],
                    isPop: companyObj['isPop'],
                    isPremium: companyObj['isPremium'],
                    isPrinted: companyObj['isPrinted']
                };
            }
            if (
                companyId &&
                [
                    this.ROLES.COMPRAS,
                    this.ROLES.CALIDAD,
                    this.ROLES.COMPRAS_SR
                ].includes(userBD.role)
            ) {
                const [companyObj, companyOptions] = await Promise.all([
                    this._clientService.getClient(companyId),
                    this._clientOptionService.getClientOption(companyId)
                ]);
                company = {
                    comertial_name:
                        companyObj?.comertial_name ?? userBD.name ?? '',
                    id: companyObj?.id ?? companyId,
                    isPop: !!companyOptions?.isPop,
                    isPremium: !!companyOptions?.isPremium,
                    isPrinted: !!companyOptions?.isPrinted,
                    allPop: !!companyOptions?.allPopRfqs,
                    allPremium: !!companyOptions?.allPremiumRfqs,
                    allPrinted: !!companyOptions?.allPrintedRfqs,
                    hasMercado: !!companyOptions?.hasMercado,
                    hasAmazon: !!companyOptions?.hasAmazon,
                    publicDashbaords: !!companyOptions?.publicDashboards
                };
            }
            this._authenticated = true;
            this._authenticatedSubject.next(true);
            // Store the user on the user service
            this._userAuthService.user = {
                id: userBD.id,
                name: userBD.name,
                email: userBD.email,
                role: userBD.role,
                clientId: userBD['companyId'],
                brandId: userBD['brandId'],
                isCoupa: false,
                company,
                coupaUrl: null,
                buyerCookie: null,
                isManagement: !!userBD.isManagement,
                isAdministration: !!userBD.isAdministration
            };
            this._lastHydratedUserId = userBD.id;
            return userBD;
        } else {
            console.log('LOGIN WITH COUPA', this._router.url);
            let credentials = this._credentialsService.credentials;
            // Store the user on the user service
            let tries = 0;
            while (!credentials) {
                console.log('WAITING CREDENTIALS');
                await this._utilService.delay(1000);
                credentials = JSON.parse(localStorage.getItem('credentials'));
                console.log(credentials);
                tries++;
                if (tries > 10) {
                    this.signOutHandler();
                    window.location.reload();
                }
            }
            this._authenticated = true;

            this._authenticatedSubject.next(true);
            this._userAuthService.user = {
                id: idToken.sub,
                name: credentials.user.name,
                email: credentials.user.email,
                role: credentials.role,
                clientId: credentials.clientId,
                isCoupa: true,
                company: credentials.company,
                coupaUrl: credentials.coupaURL,
                buyerCookie: credentials.buyerCookie
            };
            this._lastHydratedUserId = idToken.sub;
            return this._userAuthService.user;
        }
    }

    /**
     * Create log User
     * Stores last login in order to track users sessions
     */
    async createLogUser(credentials: {
        user: string;
        keyword: string;
    }): Promise<any> {
        const response = await this._logsPUserService.createLogsPUsers(
            credentials
        );
        return response;
    }

    /**
     * Set failed login
     * When a user introduces a wrong password, increase tue number of failed logins
     */
    async setFailedLogin(email: string): Promise<any> {
        try {
            const users: User[] = await this._userService.getUserEmail(email);
            if (users[0]) {
                const input: UpdateUserInput = {
                    id: users[0].id,
                    failedLogins:
                        users[0].failedLogins === null
                            ? 1
                            : users[0].failedLogins + 1
                };
                await this._userService.updateUser(input);
            }
        } catch (err) {
            console.error(err);
        }
    }

    /**
     * Sign out
     */
    signOutHandler(): Observable<any> {
        return from(this.clearLocalAuthState().then(() => true));
    }

    private async clearLocalAuthState(): Promise<void> {
        this.clearCachedSession();
        this._authenticated = false;
        this._checkInFlight$ = null;
        this._setCredentialsPromise = null;
        this._setCredentialsUserId = null;
        this._lastHydratedUserId = null;
        this._userAuthService.user = null;
        this._userService.clearSessionCache();
        this._credentialsService.removeCredentials();
        this._injector.get(UserClientOptionService).clear();
        this._injector.get(UserBrandSettingPreloadService).clear();
        this._injector.get(PremiumSessionCartService).clear();
        this._authenticatedSubject.next(false);
        try {
            await signOut();
        } catch {}
        AuthUtils.clearCognitoLocalStorage(
            awsConfig.aws_user_pools_web_client_id
        );
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        if (!this.hasStoredSession()) {
            this._authenticated = false;
            this._authenticatedSubject.next(false);
            return of(false);
        }

        if (
            this._authenticated &&
            this._credentialsService.credentialsCurrentUser
        ) {
            return of(true);
        }

        if (this._checkInFlight$) {
            return this._checkInFlight$;
        }

        this._checkInFlight$ = this.runCheckInternal().pipe(
            finalize(() => {
                this._checkInFlight$ = null;
            }),
            shareReplay(1)
        );

        return this._checkInFlight$;
    }

    private runCheckInternal(): Observable<boolean> {
        const existingUser = this._credentialsService.credentialsCurrentUser;

        return new Observable<boolean>(observer => {
            this.getCachedAuthSession()
                .then(({ tokens }) => {
                    this._accessToken = tokens.accessToken.toString();
                    const tokenSub = tokens.idToken?.payload?.sub as
                        | string
                        | undefined;

                    if (
                        !this._accessToken ||
                        AuthUtils.isTokenExpired(this._accessToken)
                    ) {
                        this._authenticated = false;
                        this._authenticatedSubject.next(false);
                        observer.next(false);
                        observer.complete();
                        return;
                    }

                    if (
                        existingUser &&
                        tokenSub &&
                        existingUser.id === tokenSub
                    ) {
                        this._authenticated = true;
                        this._authenticatedSubject.next(true);
                        this._lastHydratedUserId = existingUser.id;
                        observer.next(true);
                        observer.complete();
                        return;
                    }

                    if (this._authenticated) {
                        observer.next(true);
                        observer.complete();
                        return;
                    }

                    this.signInUsingToken().subscribe({
                        next: () => {
                            this._authenticated = true;
                            this._authenticatedSubject.next(true);
                            observer.next(true);
                            observer.complete();
                        },
                        error: () => {
                            this._authenticated = false;
                            this._authenticatedSubject.next(false);
                            observer.next(false);
                            observer.complete();
                        }
                    });
                })
                .catch(() => {
                    this._authenticated = false;
                    this._authenticatedSubject.next(false);
                    observer.next(false);
                    observer.complete();
                });
        });
    }
    async registerWithDomain(user: {
        name: string;
        email: string;
    }): Promise<any> {
        try {
            // 1. Extraer dominio
            const domain = this.extractDomain(user.email);

            // 2. Buscar clientSetting
            const clientSetting = await this._clientSettingService.getClientSettingByDomain(
                domain
            );

            // 3. Obtener clientId (o default)
            const clientId =
                clientSetting?.id || environment.sourceDataClientId;

            // 4. Generar password automática
            const password =
                Math.random()
                    .toString(36)
                    .slice(-8) + 'A1!';

            // 5. Registrar en Amplify
            const result = await import('aws-amplify/auth').then(({ signUp }) =>
                signUp({
                    username: user.email,
                    password,
                    options: {
                        userAttributes: {
                            email: user.email,
                            name: user.name,
                            'custom:role': this.ROLES.COMPRAS,
                            'custom:companyId': clientId
                        }
                    }
                })
            );

            console.log('Registro exitoso:', result);

            return result;
        } catch (error) {
            console.error('Error en registro:', error);
            throw error;
        }
    }
}
